import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Flex } from 'grid-styled';
import { Map, List } from 'immutable';
import RenderPrice from '../components/RenderPrice';
import { analyticsProductClickEvent } from '../util/analyticEvents';
import { dynamicFontSize, ellipsis, fontFamilyHeading } from '../styles/helpers/mixins';
import MainProductImage from '../components/MainProductImage';
import { animation, responsiveListPadding as rlp, size } from '../styles/helpers/variables';
import { ProductVariant } from '../styles/ProductVariant';
import { ProductTag } from '../styles/ProductTag';
import { ProductImage } from '../styles/ProductImage';
import { SubHeading } from '../styles/SubHeading';
import { SITE_ROOT_URL } from '../constants/endpoints';
import { MATERIAL_IMAGE } from '../constants/imageTypes';

const GetItemTag = item => {
  const defaultVariant = item.get('variants').find(variant => variant.get('id') === item.get('defaultVariantId'));
  const tags = defaultVariant.get('tags', List());

  if (!tags.isEmpty()) {
    return (
      <ProductTagContainer>
        {tags.map(tag => (
          <ProductTag key={tag.get('key')} color={tag.get('key')}>
            {tag.get('value')}
          </ProductTag>
        ))}
      </ProductTagContainer>
    );
  }

  return false;
};

const ProductCard = props => {
  const { item, isHighlightCard, showVariantImage, activeFilterColor } = props;

  if (!item.get('url')) return false;

  const itemTag = GetItemTag(item);

  // If the product has no variants, assume it's a product variant and render the card for a variant
  if (!props.item.get('variants', List).size) {
    return (
      <ProductCardLink
        key={item.get('id')}
        to={item.get('url')}
        onClick={() => {
          analyticsProductClickEvent('productClick', 'Category Page', item);
        }}
      >
        <ProductCardContainer>
          <MainProductImage insideList>
            {itemTag}
            <ProductImage
              src={SITE_ROOT_URL + item.getIn(['images', 0, 'url'])}
              alt={item.getIn(['images', 0, 'alt'])}
            />
          </MainProductImage>
          <Container>
            <SubHeading small uppercase title={item.get('manufacturer')}>
              {item.getIn(['brand', 'name'])}
            </SubHeading>
            <ProductCardTitle title={item.get('name')}>{item.get('name')}</ProductCardTitle>
            <RenderPrice small fontWeight="600" price={item.get('price')} originalPrice={item.get('originalPrice')} />
          </Container>
        </ProductCardContainer>
      </ProductCardLink>
    );
  }

  const cheapestVariant = item
    .get('variants')
    .sortBy(variant => variant.get('price'))
    .take(1)
    .get(0);

  // check if product variants all the same price, or if they are different
  const usePriceFrom =
    props.item.get('variants').filter(variant => variant.get('price') === cheapestVariant.get('price')).size !==
    props.item.get('variants').size;

  if (isHighlightCard) {
    return (
      <Flex flexDirection="row-reverse">
        <ProductListHighlightContainer>
          <MainProductImage insideList>
            {itemTag}
            {item.get('variants') && <ProductVariant>{item.get('variants')}</ProductVariant>}
            <ProductImage
              src={SITE_ROOT_URL + item.getIn(['mainImage', 'url'])}
              alt={item.getIn(['mainImage', 'alt'])}
            />
          </MainProductImage>
          <Container>
            <SubHeading small uppercase title={item.get('manufacturer')}>
              {item.get('manufacturer')}
            </SubHeading>
            <ProductCardTitle title={item.get('name')}>{item.get('name')}</ProductCardTitle>
            <RenderPrice small fontWeight="600" price={item.get('price')} originalPrice={item.get('originalPrice')} />
          </Container>
        </ProductListHighlightContainer>
        <HighlightImage />
      </Flex>
    );
  }

  let productImage = SITE_ROOT_URL + item.getIn(['mainImage', 'url']);
  let productImageAlt = item.getIn(['mainImage', 'alt']);
  const mainImageIsMaterialImage =
    item
      .get('variants')
      .find(variant => variant.get('id') === item.get('defaultVariantId'))
      .getIn(['mainImage', 'type']) === MATERIAL_IMAGE;

  const activeUrl = activeFilterColor
    ? item
        .get('variants')
        .find(variant => variant.get('filterColor') === activeFilterColor)
        .get('url')
    : item.get('url');

  if (showVariantImage && !mainImageIsMaterialImage && !item.get('allVariantsHaveSameRelevance')) {
    const variantImage = item.get('variants').find(variant => variant.get('id') === item.get('defaultVariantId'));
    productImage = SITE_ROOT_URL + variantImage.getIn(['mainImage', 'url']);
    productImageAlt = variantImage.getIn(['mainImage', 'alt']);
  }

  if (activeFilterColor && !mainImageIsMaterialImage) {
    const activeFilterColorVariant = item
      .get('variants')
      .find(variant => variant.get('filterColor') === activeFilterColor);
    productImage = SITE_ROOT_URL + activeFilterColorVariant.getIn(['mainImage', 'url']);
    productImageAlt = activeFilterColorVariant.getIn(['mainImage', 'alt']);
  }

  if (props.simpleProductCard) {
    return (
      <ProductCardLink
        to={activeUrl}
        onClick={() => {
          analyticsProductClickEvent('productClick', 'Recently Viewed', item);
        }}
      >
        <ProductCardContainer simpleProductCard>
          <MainProductImage insideList>
            <ProductImage src={productImage} alt={productImageAlt} />
          </MainProductImage>
          <Container>
            <ProductCardTitle title={item.get('name')}>{item.get('name')}</ProductCardTitle>
            <RenderPrice
              small
              fontWeight="600"
              priceFrom={usePriceFrom}
              price={cheapestVariant.get('price')}
              originalPrice={cheapestVariant.get('originalPrice')}
            />
          </Container>
        </ProductCardContainer>
      </ProductCardLink>
    );
  }

  return (
    <ProductCardLink
      to={activeUrl}
      onClick={() => {
        analyticsProductClickEvent('productClick', 'Category Page', item);
      }}
      onMouseEnter={
        props.onProductCardHover
          ? () =>
              props.onProductCardHover(
                true,
                item
                  .get('variants')
                  .find(variant => variant.get('id') === item.get('defaultVariantId'))
                  .get('filterColor')
              )
          : undefined
      }
      onMouseLeave={props.onProductCardHover ? () => props.onProductCardHover(false) : undefined}
    >
      <ProductCardContainer>
        <MainProductImage insideList>
          {itemTag}
          {item.get('variants').size > 1 && <ProductVariant>{item.get('variants').size} varianter</ProductVariant>}
          <ProductImage src={productImage} alt={productImageAlt} />
        </MainProductImage>
        <Container>
          <SubHeading uppercase title={item.get('manufacturer')}>
            {item.getIn(['brand', 'name'])}
          </SubHeading>
          <ProductCardTitle title={item.get('name')}>{item.get('name')}</ProductCardTitle>
          <RenderPrice
            small
            fontWeight="600"
            priceFrom={usePriceFrom}
            price={cheapestVariant.get('price')}
            originalPrice={cheapestVariant.get('originalPrice')}
          />
        </Container>
      </ProductCardContainer>
    </ProductCardLink>
  );
};

export const Container = styled.div`
  text-align: center;
`;

export const ProductCardTitle = styled.p`
  ${fontFamilyHeading};
  ${dynamicFontSize(14, 22)};
  margin: 0.25em 0;
  line-height: 1.3;
  ${ellipsis};
`;

export const ProductTagContainer = styled.div`
  position: absolute;
  display: flex;
  flex-wrap: wrap;
  width: 60%;
  top: 10px;
  left: 8px;
  z-index: 1;
  @media screen and (min-width: ${size.medium}px) {
    top: 11px;
    left: 11px;
  }
`;

export const ProductCardContainer = styled.div`
  background-color: #fff;
  margin: 0;
  padding: 0 0 10px;
  position: relative;
  height: 100%;

  &:after {
    content: '';
    transition: opacity 0.4s ${animation.easing};
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.2);
    opacity: 0;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  ${props =>
    props.simpleProductCard &&
    css`
      padding: 0;
      > div {
        &:first-child {
          > div {
            padding: 10px;
            > img {
              margin: 0;
            }
          }
        }
        &:last-child {
          padding: 0 10px 20px;
          > p {
            margin: 0 0 5px;
            padding: 0;
            font-family: 'Open Sans';
            font-size: 12px;
            line-height: 1.25;
            @media screen and (min-width: 1180px) {
              font-size: 14px;
            }
          }
          > div {
            margin: 0;
            > p {
              margin: 0;
              font-size: 12px;
              line-height: 1.25;
              @media screen and (min-width: 1180px) {
                font-size: 14px;
              }
              > span {
                font-size: 10px;
              }
            }
          }
        }
      }
    `};
`;

export const ProductCardLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  transition: opacity ${animation.transitionTime} ${animation.easing};

  &:hover {
    opacity: 0.8;
    ${ProductCardContainer}:after {
      opacity: 1;
    }
  }
`;

const ProductListHighlightContainer = styled.div`
  background-color: #fff;
  margin: 0;
  padding: 0;

  width: calc(50% - ${rlp.rlpSmall});
  @media (min-width: 640px) {
    width: calc(50% - ${rlp.rlpMedium});
  }
  @media (min-width: 832px) {
    width: calc(50% - ${rlp.rlpLarge});
  }
  @media (min-width: 1024px) {
    width: calc(50% - ${rlp.rlpXLarge});
  }
`;

const HighlightImage = styled.div`
  background-size: cover;
  background-image: url(/images/sofa-in-room.png);
  background-repeat: no-repeat;
  background-position: center;

  width: calc(50% + ${rlp.rlpSmall});
  @media (min-width: 640px) {
    width: calc(50% + ${rlp.rlpMedium});
  }
  @media (min-width: 832px) {
    width: calc(50% + ${rlp.rlpLarge});
  }
  @media (min-width: 1024px) {
    width: calc(50% + ${rlp.rlpXLarge});
  }
`;

ProductCard.defaultProps = {
  isHighlightCard: false,
  onProductCardHover: null,
  showVariantImage: false,
  activeFilterColor: null,
  simpleProductCard: false
};

ProductCard.propTypes = {
  item: PropTypes.instanceOf(Map).isRequired,
  onProductCardHover: PropTypes.func,
  isHighlightCard: PropTypes.bool,
  showVariantImage: PropTypes.bool,
  activeFilterColor: PropTypes.string,
  simpleProductCard: PropTypes.bool
};

export default ProductCard;



// WEBPACK FOOTER //
// ./src/components/ProductCard.js